import { ShadowTypeEnum } from '@/utils/enums/shadowType.enum';

/**
 * A drop shadow is a raster effect: texts render it into the bitmap and image placeholders cast it
 *  with a canvas filter. Either way it only survives the formats that are exported as an image, so
 *  the vector ones (eps, ai, dxf, ofm, emb, dst) would trace the element and silently drop it.
 *
 * Every shadow panel shares this rule, and step 1 mirrors it the other way around: once any element
 *  has a shadow those formats are no longer selectable (see checkIfAnyElementHasAShadow).
 */
const SHADOW_EXPORT_FORMATS = ['png', 'jpg', 'pdf'];

/** True while the template is exported to a format that can actually render a shadow. */
export const exportFormatSupportsShadow = (exportFormat?: string) =>
    !exportFormat || SHADOW_EXPORT_FORMATS.includes(exportFormat);

/** The shadow a canvas element casts. Every element that supports shadows stores exactly this. */
export interface ShadowState {
    /** null means no shadow at all */
    shadowType: ShadowTypeEnum | null;
    /** % of the element's size the shadow is displaced by */
    shadowOffset: number;
    /** direction of the displacement, in degrees */
    shadowAngle: number;
    /** % of blur, only meaningful on ShadowTypeEnum.Blurred */
    shadowBlur: number;
}

export interface ShadowPatch extends Partial<ShadowState> {
    /**
     * The css the shadow is painted with, already resolved from the selected color option.
     * Travels with the rest of the patch so elements that rebuild an expensive filter do it once.
     */
    shadowFill?: string;
}

/** Values a shadow is turned on with when the merchant hasn't set them yet. */
export const SHADOW_DEFAULTS = {
    shadowOffset: 50,
    shadowAngle: 45,
    shadowBlur: 20
};

export const NO_SHADOW: ShadowState = {
    shadowType: null,
    shadowOffset: 0,
    shadowAngle: 0,
    shadowBlur: 0
};

/**
 * The whole rule behind the shadow type thumbnails, kept pure so every element behaves the same:
 *  clicking the selected type again clears the shadow, turning one on fills in the defaults the
 *  merchant hasn't set, and blur only exists on the blurred variant.
 */
export const nextShadowState = (current: ShadowState, clicked: ShadowTypeEnum): ShadowState => {
    if (current.shadowType === clicked) {
        return { ...NO_SHADOW };
    }

    return {
        shadowType: clicked,
        shadowOffset: current.shadowOffset || SHADOW_DEFAULTS.shadowOffset,
        shadowAngle: current.shadowAngle || SHADOW_DEFAULTS.shadowAngle,
        shadowBlur: clicked === ShadowTypeEnum.Blurred ? current.shadowBlur || SHADOW_DEFAULTS.shadowBlur : 0
    };
};
